import { useEffect, useState, useCallback } from "react";
import { FileText, Download, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DOC_TYPES = { KTP: "KTP", KK: "Kartu Keluarga", NPWP: "NPWP", CV: "CV", PKWT: "PKWT", PKWTT: "PKWTT", NDA: "NDA", JOB_DESC: "Job Description", SK: "Surat Keputusan", LAINNYA: "Lainnya" };
const STATUS = {
  VALID: { l: "Valid", c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
  EXPIRING: { l: "Segera Berakhir", c: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300" },
  EXPIRED: { l: "Kadaluarsa", c: "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" },
};

export function EmployeeDocuments({ empId, canWrite }) {
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("KTP");
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/employees/${empId}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  }, [empId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return toast.error("Pilih file dokumen");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("document_type", type);
      fd.append("expiry_date", expiry);
      await api.post(`/employees/${empId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokumen berhasil diunggah");
      setFile(null); setExpiry("");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Gagal mengunduh dokumen"); }
  };

  const del = async (id) => { try { await api.delete(`/documents/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="border-t border-slate-100 pt-4 dark:border-slate-800" data-testid="employee-documents">
      <p className="mb-3 flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-primary" /> Dokumen Karyawan</p>

      {canWrite && (
        <div className="mb-3 grid grid-cols-1 gap-2 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/40 sm:grid-cols-[auto_1fr_auto_auto]">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="doc-type-select" className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="doc-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} />
          <div className="flex items-center gap-1">
            <Label className="whitespace-nowrap text-xs text-slate-400">Kedaluwarsa</Label>
            <Input data-testid="doc-expiry-input" type="date" className="w-36" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
          </div>
          <Button data-testid="doc-upload-button" onClick={upload} disabled={busy} className="gap-1">
            <Upload className="h-4 w-4" /> {busy ? "..." : "Unggah"}
          </Button>
        </div>
      )}

      <div className="space-y-2">
        {docs.length === 0 && <p className="text-sm text-slate-400">Belum ada dokumen.</p>}
        {docs.map((d) => {
          const st = STATUS[d.status] || STATUS.VALID;
          return (
            <div key={d.id} data-testid="document-row" className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 dark:border-slate-800">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">{d.type_label || d.document_type}</span>
                  <span className="truncate text-sm font-medium">{d.original_filename}</span>
                  {d.expiry_date && <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${st.c}`}>{st.l}</span>}
                </div>
                {d.expiry_date && <p className="mt-0.5 text-xs text-slate-400">Berlaku s/d {fmtDate(d.expiry_date)}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" data-testid="doc-download-button" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                {canWrite && <Button size="icon" variant="ghost" data-testid="doc-delete-button" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
